import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getStoredConsent,
  setStoredConsent,
  CONSENT_GRANTED,
  CONSENT_DENIED,
} from "../utils/consentHelper";
import {
  isGaConfigured,
  initGa,
  disableGa,
} from "../utils/analytics";
import ConsentBanner from "../components/ConsentBanner";

/**
 * Cookie/analytics consent provider — the GDPR gate in front of GA4.
 *
 * Mirrors the conditional-provider pattern in `contexts/RecaptchaContext.jsx`:
 *   - When GA is NOT configured (flag off / id missing / Do-Not-Track), this
 *     provider is an inert pass-through: no banner renders, no script loads,
 *     consent state stays at "denied", and the context value is a no-op.
 *   - When GA IS configured, it shows the accessible consent banner until the
 *     user makes a choice, persists that choice, and only calls `initGa()`
 *     AFTER explicit consent === "granted".
 *
 * GA is therefore NEVER initialized before consent, and the banner itself
 * loads zero analytics.
 */

const ConsentContext = createContext({
  consent: CONSENT_DENIED,
  hasChoice: false,
  gaConfigured: false,
  showBanner: false,
  acceptConsent: () => {},
  rejectConsent: () => {},
});

export function ConsentProvider({ children }) {
  // Read whether GA is even on the table once (env + DNT are static per load).
  const gaConfigured = isGaConfigured();

  // Stored choice: "granted" | "denied" | null (= no choice made yet).
  const [consent, setConsent] = useState(() => getStoredConsent());

  // The banner only shows when GA is configured AND there's no prior choice.
  const hasChoice = consent === CONSENT_GRANTED || consent === CONSENT_DENIED;
  const showBanner = gaConfigured && !hasChoice;

  // Initialize GA after (and only after) explicit consent.
  useEffect(() => {
    if (!gaConfigured) {
      return;
    }
    if (consent === CONSENT_GRANTED) {
      initGa();
    } else if (consent === CONSENT_DENIED) {
      disableGa();
    }
  }, [consent, gaConfigured]);

  // #514 — the banner is `position:fixed; bottom:0` and full-width, so on a
  // fixed-viewport app shell (the Edit-Wheel builder, a 92vh shell) it renders
  // OVER the builder's own footer action row (+ Segment / Show Label) and the
  // bottom of the side-pane — a stark white bar across the dark canvas that also
  // HIDES those controls (MEASURED live on web-stg /builder, bundle
  // main.7731603d.js). Publish the banner's live height as a CSS custom property
  // on :root while (and only while) the banner is shown; the builder subtracts it
  // from its 92vh shells via `calc(92vh - var(--iyuga-consent-reserve, 0px))`, so
  // its bottom chrome sits ABOVE the banner. Only elements that reference the var
  // react — every other surface is untouched — and the var is REMOVED when the
  // banner is gone, so once a choice is made the layout is byte-identical to today.
  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const root = document.documentElement;
    const clear = () => root.style.removeProperty("--iyuga-consent-reserve");
    if (!showBanner) {
      clear();
      return clear;
    }
    let frame = 0;
    const apply = () => {
      frame = 0;
      const el = document.querySelector(".iyuga-consent-banner");
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      root.style.setProperty("--iyuga-consent-reserve", `${h}px`);
    };
    const schedule = () => {
      if (frame) {
        return;
      }
      frame =
        typeof requestAnimationFrame === "function"
          ? requestAnimationFrame(apply)
          : (apply(), 0);
    };
    apply();
    const el = document.querySelector(".iyuga-consent-banner");
    let observer = null;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(schedule);
      observer.observe(el);
    }
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      if (observer) {
        observer.disconnect();
      }
      if (frame && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frame);
      }
      clear();
    };
  }, [showBanner]);

  const acceptConsent = useCallback(() => {
    setStoredConsent(CONSENT_GRANTED);
    setConsent(CONSENT_GRANTED);
  }, []);

  const rejectConsent = useCallback(() => {
    setStoredConsent(CONSENT_DENIED);
    setConsent(CONSENT_DENIED);
  }, []);

  const value = {
    consent: consent || CONSENT_DENIED,
    hasChoice,
    gaConfigured,
    // Exposed so callers can defer their OWN modals/dialogs until the consent
    // choice is made (see ExploreV2/index.jsx) — the banner is a fixed,
    // bottom-anchored, always-topmost-among-page-chrome dialog (see
    // ConsentBanner.zIndexStacking.test.js), so anything that opens a HIGHER
    // z-index modal (like a first-run onboarding dialog) while showBanner is
    // true will visually and pointer-event-wise bury the banner underneath
    // its own backdrop, making the legally-required consent choice
    // unreachable. Consumers should treat showBanner as a gate, not just a
    // render flag.
    showBanner,
    acceptConsent,
    rejectConsent,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {showBanner ? (
        <ConsentBanner
          onAccept={acceptConsent}
          onReject={rejectConsent}
        />
      ) : null}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  return useContext(ConsentContext);
}
